import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Button, Flex, Typography, Spin } from "antd"
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnUnderline, BtnBulletList, BtnNumberedList, BtnUndo, BtnRedo } from "react-simple-wysiwyg"
import { useTranslation } from "react-i18next"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import styles from "./index.module.css"

const { Title } = Typography

const RichTextEditor = ({ value, onChange }) => (
  <EditorProvider>
    <Editor className={styles.templateEditor} value={value} onChange={onChange}>
      <Toolbar>
        <BtnUndo />
        <BtnRedo />
        <BtnBold />
        <BtnItalic />
        <BtnUnderline />
        <BtnBulletList />
        <BtnNumberedList />
      </Toolbar>
    </Editor>
  </EditorProvider>
)

RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
}

const toEditorHtml = (val) => (val || "").replaceAll("\n", "<br>")

const EditTemplateModal = ({ item, content, open, onClose, onSave, loading, saving }) => {
  const { t } = useTranslation()
  const [messageBody, setMessageBody] = useState("")
  const [footer, setFooter] = useState("")
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const initialRef = useRef({ messageBody: "", footer: "" })

  useEffect(() => {
    if (!open || !item) return
    if (loading) return
    const initial = {
      messageBody: toEditorHtml(content?.message_body ?? item.message_body ?? ""),
      footer: toEditorHtml(content?.footer ?? item.footer ?? "")
    }
    initialRef.current = initial
    setMessageBody(initial.messageBody)
    setFooter(initial.footer)
  }, [open, item, content, loading])

  const isTouched = () =>
    messageBody !== initialRef.current.messageBody ||
    footer !== initialRef.current.footer

  const requestClose = () => {
    if (isTouched()) {
      setShowUnsavedModal(true)
    } else {
      executeClose()
    }
  }

  const executeClose = () => {
    setMessageBody("")
    setFooter("")
    setShowUnsavedModal(false)
    onClose()
  }

  const handleFinish = () => {
    onSave(item.id, messageBody, footer)
  }

  const handleTokenClick = (token) => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      const container = range.startContainer
      const editorEl = container.nodeType === Node.TEXT_NODE ? container.parentElement : container
      if (editorEl?.closest?.("[contenteditable]")) {
        editorEl.closest("[contenteditable]").focus()
        document.execCommand("insertText", false, token + " ")
        return
      }
    }
    setMessageBody((prev) => prev + token)
  }

  const DEFAULT_TOKENS = [
    { label: t("communication_module.edit_template.token_first_name"), token: "[FIRST_NAME]" },
    { label: t("communication_module.edit_template.token_last_name"), token: "[LAST_NAME]" }
  ]
  const tokens = DEFAULT_TOKENS

  return (
    <>
      <Modal
        open={open}
        onCancel={requestClose}
        footer={null}
        width={700}
        maskClosable={false}
        centered
        destroyOnClose
      >
        <Title level={4} className="mb-m">
          {item?.name || t("communication_module.edit_template.title")}
        </Title>

        {loading ? (
          <Flex justify="center" align="center" className={styles.spinContainer}>
            <Spin />
          </Flex>
        ) : (
          <Form layout="vertical" onFinish={handleFinish}>
            {tokens.length > 0 && (
              <Form.Item label={t("communication_module.edit_template.tokens_label")}>
                <Flex gap={8} wrap="wrap">
                  {tokens.map((tok) => (
                    <button
                      key={tok.token}
                      type="button"
                      className={styles.tokenChip}
                      onClick={() => handleTokenClick(tok.token)}
                    >
                      {tok.label}
                    </button>
                  ))}
                </Flex>
              </Form.Item>
            )}

            <Form.Item label={t("communication_module.edit_template.body_label")}>
              <RichTextEditor value={messageBody} onChange={(e) => setMessageBody(e.target.value)} />
            </Form.Item>

            <Form.Item label={t("communication_module.edit_template.footer_label")}>
              <RichTextEditor value={footer} onChange={(e) => setFooter(e.target.value)} />
            </Form.Item>

            <Flex gap={8} justify="flex-end" className="mt-m">
              <Button onClick={requestClose}>
                {t("communication_module.edit_template.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                {t("communication_module.edit_template.save")}
              </Button>
            </Flex>
          </Form>
        )}
      </Modal>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onKeepEditing={() => setShowUnsavedModal(false)}
        onDiscard={executeClose}
      />
    </>
  )
}

EditTemplateModal.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    message_body: PropTypes.string,
    footer: PropTypes.string
  }),
  content: PropTypes.shape({
    message_body: PropTypes.string,
    footer: PropTypes.string
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  saving: PropTypes.bool
}

export default EditTemplateModal
